/**
 * agency-referrals (design §9.1): the promoter link. A promoter shares `<portal>/signup?ref=CODE`; at boot,
 * before anything renders (so no redirect can drop the query), the portal stores the code in localStorage
 * `coreway.ref` with its capture time and removes `ref` from the address bar. First touch wins: an unexpired
 * stored code is never replaced (TTL 30 days). The sign-up form prefills its optional "Promoter code" field
 * from it; the server decides whether the code attaches.
 *
 * Only `ref` is read or removed. `code` (the auth return) is never touched. Every storage and history access
 * is guarded: a browser that refuses them simply has no stored code.
 */
export interface ReferralRef {
  code: string;
  at: number;
}

export const REFERRAL_REF_KEY = 'coreway.ref';
export const REFERRAL_REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_SHAPE = /^[A-Z0-9]{4,16}$/;

interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
}

interface HistoryLike {
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function browserHistory(): HistoryLike | null {
  try {
    return window.history;
  } catch {
    return null;
  }
}

function parse(raw: string | null, now: number): ReferralRef | null {
  if (!raw) return null;
  let v: Partial<ReferralRef> | null;
  try {
    v = JSON.parse(raw) as Partial<ReferralRef> | null;
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object') return null;
  if (typeof v.code !== 'string' || !CODE_SHAPE.test(v.code)) return null;
  if (typeof v.at !== 'number' || !Number.isFinite(v.at)) return null;
  if (now < v.at || now - v.at > REFERRAL_REF_TTL_MS) return null;
  return { code: v.code, at: v.at };
}

function readRaw(storage: Storage | null): string | null {
  try {
    return storage?.getItem(REFERRAL_REF_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Reads `?ref=` on any path, stores it (first touch, shape-checked), and strips it from the URL. */
export function captureReferralRef(
  loc: LocationLike = window.location,
  now: number = Date.now(),
  storage: Storage | null = localStore(),
  history: HistoryLike | null = browserHistory(),
): void {
  const params = new URLSearchParams(loc.search);
  if (!params.has('ref')) return;
  const code = (params.get('ref') ?? '').trim().toUpperCase();
  if (CODE_SHAPE.test(code) && !parse(readRaw(storage), now)) {
    try {
      storage?.setItem(REFERRAL_REF_KEY, JSON.stringify({ code, at: now }));
    } catch {
      // No storage: the founder can still type the code.
    }
  }
  params.delete('ref');
  const query = params.toString();
  try {
    history?.replaceState(null, '', `${loc.pathname}${query ? `?${query}` : ''}${loc.hash}`);
  } catch {
    // The address bar keeps the ref; nothing else depends on it.
  }
}

/** The stored code, or null when absent, malformed, expired, or storage refuses. */
export function readReferralRef(now: number = Date.now(), storage: Storage | null = localStore()): string | null {
  return parse(readRaw(storage), now)?.code ?? null;
}

/** Forget the stored code (after the agency is created). */
export function clearReferralRef(storage: Storage | null = localStore()): void {
  try {
    storage?.removeItem(REFERRAL_REF_KEY);
  } catch {
    // Nothing to forget.
  }
}
